import React from 'react';
import './OrdersTable.css';
import Pagination from './Pagination';
import MessageDetailsPanel from './MessageDetailsPanel';
import useUrlDetails from '../hooks/useUrlDetails';

const MessagesTable = ({ data, pagination, onPageChange, showTraderName = false }) => {

  // Extract data - support both the new paginated format and the old format
  let messages = [];
  
  if (data && data.data && Array.isArray(data.data)) {
    // New paginated format
    messages = data.data;
  } else if (Array.isArray(data)) {
    // Old format - just an array
    messages = data;
  } else if (data && data.ops && data.ops.length > 0) {
    // Backward compatibility with the old structure
    const firstOp = data.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      messages = firstOp.list;
    } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      messages = firstOp.data.list;
    }
  }

  const [selectedMessage, setSelectedMessage] = useUrlDetails(messages, 'messageId');

  // Sort by creation date (create_time) - newest first
  messages.sort((a, b) => {
    const timeA = a.create_time || 0;
    const timeB = b.create_time || 0;
    return timeB - timeA;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000); // If timestamp is in seconds
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp); // Try as milliseconds
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: '1rem', fontWeight: 600 }}>{day}.{month}.{year}</span>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  // Get all unique keys from the data for dynamic display
  const getAllKeys = () => {
    const keysSet = new Set();
    messages.forEach(message => {
      Object.keys(message).forEach(key => {
        // Exclude service fields
        if (key !== 'ref' && key !== 'create_time' && key !== 'change_time') {
          keysSet.add(key);
        }
      });
    });
    return Array.from(keysSet);
  };

  // Determine the columns to display
  const allKeys = getAllKeys();
  const displayColumns = [
    { 
      key: 'Created', 
      label: 'Created', 
      getValue: (message) => {
        const created = message.create_time || null;
        return formatDate(created);
      }
    },
    ...allKeys.map(key => ({
      key,
      label: key,
      getValue: (message) => {
        const value = message[key];
        if (value === null || value === undefined) return '-';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      }
    }))
  ];

  if (messages.length === 0) {
    return (
      <div className="orders-empty">
        <p>No data to display</p>
      </div>
    );
  }

  return (
    <>
      <div className="orders-table-container">
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                {displayColumns.map((column) => (
                  <th key={column.key} className={`header-${column.key.toLowerCase()}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {messages.map((message, index) => {
                const status = (message.status || message.Status || '').toLowerCase();
                return (
                  <tr 
                    key={message.id || message.ref || index}
                    className="order-row"
                    onClick={() => setSelectedMessage(message)}
                  >
                    {displayColumns.map((column) => {
                      const isStatusColumn = column.key === 'Status' || column.key === 'status';
                      const statusClass = isStatusColumn ? `status-${status}` : '';
                      return (
                        <td 
                          key={column.key} 
                          className={`column-${column.key.toLowerCase()} ${statusClass}`}
                          data-label={column.label}
                        >
                          {column.getValue(message)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination && onPageChange && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>
      <MessageDetailsPanel 
        message={selectedMessage} 
        onClose={() => setSelectedMessage(null)}
      />
    </>
  );
};

export default MessagesTable;
